import { useTranslation } from "@coworker/locales";
import {
  FilterType,
  FilterValue,
  InputType,
  SelectFilter,
} from "../../shared/filters/types/filter";
import { getSelectedFilterValue } from "../../shared/filters/utils/filter/filtervalue";
import { TASK_TYPE_ID, SORT_BY_ID } from "../constants/filterIds";
import { useTaskTypeFilterConfig } from "./useTaskTypeFilterConfig";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

const ALL_SORT_OPTIONS = [
  { value: "smart", key: "smartString" },
  { value: "createdAtDesc", key: "newestFirstString" },
  { value: "createdAtAsc", key: "oldestFirstString" },
  { value: "gridCodeDesc", key: "gridCodeDescendingString" },
  { value: "gridCodeAsc", key: "gridCodeAscendingString" },
  { value: "pickupGridCodeDesc", key: "pickupGridCodeDescendingString" },
  { value: "pickupGridCodeAsc", key: "pickupGridCodeAscendingString" },
  { value: "refillGridCodeDesc", key: "refillGridCodeDescendingString" },
  { value: "refillGridCodeAsc", key: "refillGridCodeAscendingString" },
];

export function useSortByFilter(filterValues: FilterValue[]): SelectFilter {
  const { t } = useTranslation();
  const enableAddonOrdersV2 = useFlag(FLAGS.ENABLE_ADDON_ORDERS_V2);
  const enableSmartSort = useFlag(FLAGS.ENABLE_KEY_SET_GET_TASKS);
  const { hasConfig, config } = useTaskTypeFilterConfig(
    SORT_BY_ID,
    filterValues
  );

  const defaultValue = {
    filterId: SORT_BY_ID,
    value: "createdAtDesc",
  };
  const selected = getSelectedFilterValue(
    SORT_BY_ID,
    filterValues,
    defaultValue
  );

  let options: { value: string; title: string; dependsOnFilterId?: string }[];
  const availableSortOptions = ALL_SORT_OPTIONS.filter(
    (option) => option.value !== "smart" || enableSmartSort
  );

  if (hasConfig && config?.options) {
    options = availableSortOptions
      .filter((opt) => config.options!.includes(opt.value))
      .map((opt) => ({
        value: opt.value,
        title: t(opt.key),
        dependsOnFilterId: TASK_TYPE_ID,
      }));
  } else {
    const taskTypeFilter = filterValues.find(
      (f) => f.filterId === TASK_TYPE_ID
    );
    const taskType = taskTypeFilter?.value as string | undefined;

    const gridCodeSortableTaskTypes = ["PQR"];
    const v1AddonTaskTypes = ["pickAndRefill", "refill"];
    const v2AddonTaskTypes = enableAddonOrdersV2 ? ["addonOrder"] : [];

    const enableGridCode =
      taskType && gridCodeSortableTaskTypes.includes(taskType);
    const enableAddonSort =
      taskType && [...v1AddonTaskTypes, ...v2AddonTaskTypes].includes(taskType);

    options = [
      enableSmartSort && { value: "smart", title: "Smart" },
      { value: "createdAtDesc", title: t("newestFirstString") },
      { value: "createdAtAsc", title: t("oldestFirstString") },
      enableGridCode && {
        value: "gridCodeDesc",
        title: t("gridCodeDescendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
      enableGridCode && {
        value: "gridCodeAsc",
        title: t("gridCodeAscendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
      enableAddonSort && {
        value: "pickupGridCodeDesc",
        title: t("pickupGridCodeDescendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
      enableAddonSort && {
        value: "pickupGridCodeAsc",
        title: t("pickupGridCodeAscendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
      enableAddonSort && {
        value: "refillGridCodeDesc",
        title: t("refillGridCodeDescendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
      enableAddonSort && {
        value: "refillGridCodeAsc",
        title: t("refillGridCodeAscendingString"),
        dependsOnFilterId: TASK_TYPE_ID,
      },
    ].filter(Boolean) as { value: string; title: string }[];
  }

  const selectedOption =
    options.find((option) => option.value === selected.value) || options[0];

  return {
    inputType: InputType.Select,
    filterType: FilterType.Sort,
    id: SORT_BY_ID,
    title: t("sortByFilterTitleString", {
      option: selectedOption?.title || "",
    }),
    options,
    enabled: true,
    defaultValue,
  };
}
